import { styled } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ConfirmationDialog } from "components/ConfirmationDialog";
import { COMMON_TRANSLATION_KEYS } from "constants/commonTranslationsKeys";

import { FILTERS_TEST_IDS } from "../../constants";
import { Params, useFilters } from "../../hooks";
import { Z_INDEX } from "../../utils";
import { FiltersButton } from "../Filters/FiltersButton";
import { FiltersModal } from "../FiltersModal";

interface FiltersContainerProps {
  children: ReactNode;
  filtersCount: number;
  endFilterComponent?: ReactNode;
}

export function FiltersContainer<FiltersModel extends Params<string>>({
  filtersCount,
  children,
  endFilterComponent = null,
}: FiltersContainerProps) {
  const {
    isActive,
    toggleActive,
    handleCloseConfirmationModal,
    handleClickAway,
    areFiltersTheSameAsInitial,
    areFiltersInitialAndDefaultDifferent,
    handleApply,
    handleReset,
    handleClose,
    isConfirmationModalActive,
    closeAllFiltersPopup,
    handleConfirm,
  } = useFilters<FiltersModel>();
  const { t } = useTranslation("common");

  return (
    <Container>
      <FiltersRow>
        <FiltersButton filtersNumber={filtersCount} onClick={toggleActive} />
        {endFilterComponent}
      </FiltersRow>
      <FiltersModal
        areFiltersTheSameAsInitial={areFiltersTheSameAsInitial}
        data-testid={FILTERS_TEST_IDS.modal}
        isActive={isActive}
        areFiltersInitialAndDefaultDifferent={
          areFiltersInitialAndDefaultDifferent
        }
        handleApply={handleApply}
        handleClickAway={handleClickAway}
        handleClose={handleClose}
        handleReset={handleReset}
      >
        {children}
      </FiltersModal>
      <ConfirmationDialog
        cancelLabel={t(COMMON_TRANSLATION_KEYS.dontApply)}
        cancelLabelColor="default"
        description={t(COMMON_TRANSLATION_KEYS.beforeYouCloseDownTheFilter)}
        okLabel={t(COMMON_TRANSLATION_KEYS.applyFilters)}
        open={isConfirmationModalActive}
        title={t(COMMON_TRANSLATION_KEYS.youHaveUnsavedFilters)}
        onCancel={closeAllFiltersPopup}
        onClose={handleCloseConfirmationModal}
        onOk={handleConfirm}
      />
    </Container>
  );
}

const Container = styled("div")({
  position: "relative",
  zIndex: Z_INDEX.FILTERS,
});

const FiltersRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(0, 0, 4),
}));
